import React from 'react';
import classNames from 'classnames';

import { useTranslation } from 'react-i18next';
import { Container, Icon, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import {
  ImmutableEntity,
} from '@uncinc/react-drupal-core/types';

import {
  dateFormatter,
} from '../../../../helpers';
import { translateEmploymentTypes } from '../../../../helpers/translatedEmploymentTypes';

import { NodeVacancy } from '../../../../types/uncinc_profile/nodes';
import {
  Breadcrumbs,
  ButtonWrapper,
  ParagraphsRenderer,
} from '../../../../components';
import { DEFAULT_LANGUAGE } from '../../../../config/constants';

import './style.scss';

interface NodeVacancyProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeVacancy>;
}

const NodeVacancyPage: React.FC<NodeVacancyProps> = ({ entity }) => {
  const { t } = useTranslation();
  const classes = classNames('Node Node-type-breadcrumbs NodeVacancyPage');
  const vacancy = entity.toJS();
  const title = vacancy.title[0].value;
  const paragraphs = vacancy.field_paragraphs;
  const body = vacancy.body[0]?.processed;
  const lang = vacancy.langcode[0]?.value || DEFAULT_LANGUAGE;
  const date = vacancy.field_publish_date[0]?.value || vacancy.created[0]?.value;
  const email = vacancy.field_email_address[0]?.value;

  const location = vacancy.field_location[0];
  const streetAddress = [
    location?.entity.field_address[0]?.address_line1,
    location?.entity.field_address[0]?.address_line2,
    location?.entity.field_address[0]?.address_line3,
  ].join(' ');

  const htmlTitle = title.replace(/ /g, '%20');
  const employmentTypes = vacancy.field_employment_types;
  const translatedEmploymentTypes = translateEmploymentTypes(
    vacancy.field_employment_types,
    lang,
  );

  const applyLink = vacancy.field_link[0]?.uri || `mailto:${email}?subject=Sollicitatie%20${htmlTitle}&body=Beste%20UncInc%2C`;
  const applyIcon = vacancy.field_link[0]?.uri ? 'north_east' : 'email';

  const andString = lang === DEFAULT_LANGUAGE ? ' en ' : ' and ';

  const hoursMinimum = vacancy.field_hours_minimum[0]?.value;
  const hoursMaximum = vacancy.field_hours_maximum[0]?.value;
  const salaryMinimum = vacancy.field_salary_minimum[0];
  const salaryMaximum = vacancy.field_salary_maximum[0];
  const validThrough = vacancy.field_valid_through;
  const salaryType = vacancy.field_salary_type[0];

  const benefits = vacancy.field_benefits;
  const requirements = vacancy.field_requirements;

  const schema = JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title,
    description: body,
    identifier: {
      '@type': 'PropertyValue',
      name: 'Unc Inc BV',
      value: '64721868',
    },
    datePosted: date,
    validThrough: validThrough[0]?.value,
    employmentType: employmentTypes.map((type) => type.value),
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Unc Inc BV',
      sameAs: 'https://www.uncinc.nl',
      logo: 'https://www.uncinc.nl/sites/default/files/2025-10/uncinc-vacancy.png',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress,
        postalCode: location?.entity.field_address[0]?.postal_code,
        addressCountry: location?.entity.field_address[0]?.country_code,
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: salaryMaximum.currency_code,
      value: {
        '@type': 'QuantitativeValue',
        minValue: salaryMinimum?.number,
        maxValue: salaryMaximum.number,
        unitText: salaryType.value,
      },
    },
  });

  return (
    <>
      <script type="application/ld+json">
        {schema}
      </script>

      <article itemType="http://schema.org/WebPage" className={classes}>
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <Breadcrumbs />
              <div className="NodeVacancyPage__hero">
                <div className="NodeVacancyPage__hero__text">
                  <h1 itemProp="name">{title}</h1>
                  {body && (
                    <WysiwygField
                      property="schema:text"
                      html={body}
                    />
                  )}
                </div>

                <div className="NodeVacancyPage__hero__meta">
                  <span className="NodeVacancyPage__hero__meta__data"><Icon name="access_time" />
                    <div>
                      {translatedEmploymentTypes.map((type, i) => (
                        <span>
                          {type}
                          {i < translatedEmploymentTypes.length - 2 && ', '}
                          {i === translatedEmploymentTypes.length - 2 && andString}
                        </span>
                      ))}
                    </div>
                  </span>
                  <span className="NodeVacancyPage__hero__meta__data"><Icon name="location_on" />{location?.entity.name[0].value}</span>
                  <span className="NodeVacancyPage__hero__meta__data"><Icon name="today" />{t('content_types.vacancy.date')} {dateFormatter(date)}</span>
                </div>

                <ButtonWrapper href={applyLink} iconName={applyIcon}>
                  <span>{t('content_types.vacancy.apply')}</span>
                </ButtonWrapper>

              </div>
            </div>
          </div>
        </Container>

        <div className="NodeVacancyPage__pointswrapper">
          <Container>
            <div className="NodeVacancyPage__content row">
              <div className="NodeVacancyPage__content__points col-12 col-md-10 offset-md-1 col-lg-5 offset-lg-1">
                <h2 className="title">{t('content_types.vacancy.what_we_offer')} </h2>
                <div className="NodeVacancyPage__content__wrapper">
                  {benefits.map((benefit) => (
                    <div className="NodeVacancyPage__content__point">
                      {benefit.entity.field_svg?.[0] && (
                        <object
                          type="image/svg+xml"
                          data={benefit.entity.field_svg[0].source}
                          width="30"
                          height="30"
                          role="presentation"
                        >
                          Icon
                        </object>
                      )}
                      <WysiwygField
                        property="schema:text"
                        html={benefit.entity.name[0].value}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="NodeVacancyPage__content__points col-12 col-md-10 offset-md-1 col-lg-5 offset-lg-0">
                <h2 className="title">{t('content_types.vacancy.what_we_search_for')} </h2>
                <div className="NodeVacancyPage__content__wrapper">
                  {requirements.map((requirement) => (
                    <div className="NodeVacancyPage__content__point">
                      {requirement.entity.field_svg?.[0] && (
                        <object
                          type="image/svg+xml"
                          data={requirement.entity.field_svg[0].source}
                          width="30"
                          height="30"
                          role="presentation"
                        >
                          Icon
                        </object>
                      )}
                      <WysiwygField
                        property="schema:text"
                        html={requirement.entity.name[0].value}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </div>

        <ParagraphsRenderer className="NodeVacancyPage__paragraphs" paragraphs={paragraphs} />
      </article>
    </>
  );
};

export default NodeVacancyPage;
